import React, { useState } from 'react';
import Icon from 'components/AppIcon';

export default function CompatibilityMatrix({ selectedChemical, selectedMaterial }) {
  const [filterType, setFilterType] = useState('all');

  const compatibilityData = [
    {
      chemical: 'Sulfuric Acid',
      material: 'Stainless Steel 316',
      rating: 'A',
      description: 'Excellent compatibility',
      color: 'bg-green-500',
    },
    {
      chemical: 'Sulfuric Acid',
      material: 'Carbon Steel',
      rating: 'D',
      description: 'Not recommended',
      color: 'bg-red-500',
    },
    {
      chemical: 'Hydrochloric Acid',
      material: 'PTFE',
      rating: 'A',
      description: 'Excellent compatibility',
      color: 'bg-green-500',
    },
    {
      chemical: 'Sodium Hydroxide',
      material: 'Polypropylene',
      rating: 'B',
      description: 'Good compatibility',
      color: 'bg-blue-500',
    },
    {
      chemical: 'Acetone',
      material: 'EPDM Rubber',
      rating: 'C',
      description: 'Fair - Limited use',
      color: 'bg-yellow-500',
    },
  ];

  const ratingLegend = [
    { rating: 'A', label: 'Excellent', color: 'bg-green-500', description: 'Recommended for continuous use' },
    { rating: 'B', label: 'Good', color: 'bg-blue-500', description: 'Suitable for most applications' },
    { rating: 'C', label: 'Fair', color: 'bg-yellow-500', description: 'Limited use - monitor regularly' },
    { rating: 'D', label: 'Poor', color: 'bg-red-500', description: 'Not recommended' },
  ];

  return (
    <div className="space-y-6">
      {/* Matrix Header */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Compatibility Matrix</h3>
            <p className="text-sm text-muted-foreground">
              Chemical-material compatibility ratings and recommendations
            </p>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary flex items-center gap-2">
              <Icon name="Filter" size={18} />
              <span className="hidden sm:inline">Filter</span>
            </button>
            <button className="btn-secondary flex items-center gap-2">
              <Icon name="Download" size={18} />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>

        {/* Rating Legend */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {ratingLegend?.map((item) => (
            <div key={item?.rating} className="p-3 bg-accent/50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-8 h-8 ${item?.color} rounded-lg flex items-center justify-center text-white font-bold`}>
                  {item?.rating}
                </div>
                <span className="font-medium text-foreground">{item?.label}</span>
              </div>
              <p className="text-xs text-muted-foreground">{item?.description}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Compatibility Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-semibold text-foreground">Chemical</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Material</th>
                <th className="text-center py-3 px-4 font-semibold text-foreground">Rating</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Description</th>
                <th className="text-center py-3 px-4 font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {compatibilityData?.map((item, index) => (
                <tr key={index} className="border-b border-border hover:bg-accent/50 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Icon name="Flask" size={16} className="text-primary" />
                      <span className="font-medium text-foreground">{item?.chemical}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Icon name="Box" size={16} className="text-blue-500" />
                      <span className="text-foreground">{item?.material}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className={`w-10 h-10 ${item?.color} rounded-lg flex items-center justify-center text-white font-bold mx-auto`}>
                      {item?.rating}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">{item?.description}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <button className="p-2 hover:bg-accent rounded-lg transition-colors" title="View details">
                        <Icon name="Eye" size={18} className="text-muted-foreground" />
                      </button>
                      <button className="p-2 hover:bg-accent rounded-lg transition-colors" title="Run test">
                        <Icon name="Play" size={18} className="text-muted-foreground" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Icon name="CheckCircle" size={20} className="text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">128</div>
              <div className="text-sm text-muted-foreground">Compatible Pairs</div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-lg">
              <Icon name="AlertTriangle" size={20} className="text-yellow-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">42</div>
              <div className="text-sm text-muted-foreground">Caution Required</div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/10 rounded-lg">
              <Icon name="XCircle" size={20} className="text-red-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">23</div>
              <div className="text-sm text-muted-foreground">Not Recommended</div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Icon name="Database" size={20} className="text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">193</div>
              <div className="text-sm text-muted-foreground">Total Tests</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}